import { useState, useEffect } from 'react';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getTenantAgents, TenantUser } from '@/lib/tenant-users-client';
import { useAssignConversation } from '@/hooks/useAssignConversation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, ChevronDown, Loader2 } from 'lucide-react';

interface AssignmentDropdownProps {
  conversationId: string;
  currentAssignedUserId?: string | null;
  currentAssignedUserName?: string | null;
  currentAssignedUserEmail?: string | null;
}

/**
 * Dropdown component for manually assigning conversations to agents
 *
 * Features:
 * - Shows current assigned agent or "Unassigned"
 * - Lists all active tenant agents
 * - i18n support (English/Spanish)
 * - NO "Unassign" option (business rule: once assigned, always assigned)
 *
 * CRITICAL BUSINESS RULE:
 * - Conversations cannot be unassigned once touched by an agent
 * - Dropdown only shows reassignment options (no NULL option)
 */
export function AssignmentDropdown({
  conversationId,
  currentAssignedUserId,
  currentAssignedUserName,
  currentAssignedUserEmail,
}: AssignmentDropdownProps) {
  const { user, tenantId } = useAuth();
  const { t } = useTranslation('conversations');
  const { assignConversation, isAssigning } = useAssignConversation();
  const [agents, setAgents] = useState<TenantUser[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Load tenant agents on mount
  useEffect(() => {
    if (!tenantId) return;

    const loadAgents = async () => {
      setIsLoadingAgents(true);
      const { data, error } = await getTenantAgents(tenantId);

      if (error) {
        console.error('[AssignmentDropdown] Error loading agents:', error);
      } else {
        setAgents(data || []);
      }

      setIsLoadingAgents(false);
    };

    loadAgents();
  }, [tenantId]);

  const handleAssignAgent = async (agentId: string, agentName: string) => {
    // Don't reassign if already assigned to this agent
    if (agentId === currentAssignedUserId) {
      return;
    }

    const success = await assignConversation(conversationId, agentId, agentName);

    if (success) {
      // Optimistic UI update will be handled by real-time subscription
      console.log(`[AssignmentDropdown] ✅ Assigned to ${agentName}`);
    }
  };

  // Get initials for avatar
  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isAssigned = !!currentAssignedUserId;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-2 px-2"
          disabled={isAssigning}
        >
          {isAssigned ? (
            <>
              <Avatar className="h-5 w-5">
                <AvatarFallback className="text-xs">
                  {getInitials(currentAssignedUserName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs max-w-[100px] truncate">
                {currentAssignedUserName}
              </span>
            </>
          ) : (
            <>
              <User className="h-4 w-4" />
              <span className="text-xs">
                {t('assignment.unassigned', 'Unassigned')}
              </span>
            </>
          )}
          {isAssigning ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[220px]">
        <DropdownMenuLabel>
          {t('assignment.assignTo', 'Assign to Agent')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isLoadingAgents ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : agents.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            {t('assignment.noAgents', 'No agents available')}
          </div>
        ) : (
          agents.map((agent) => {
            const isCurrentAgent = agent.public_id === currentAssignedUserId;
            const isCurrentUser = agent.id === user?.id;

            return (
              <DropdownMenuItem
                key={agent.id}
                onClick={() => handleAssignAgent(agent.id, agent.full_name)}
                disabled={isCurrentAgent || isAssigning}
                className="gap-2"
              >
                <Avatar className="h-5 w-5">
                  <AvatarFallback className="text-xs">
                    {getInitials(agent.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm truncate">
                    {agent.full_name}
                    {isCurrentUser && (
                      <span className="text-muted-foreground ml-1">
                        ({t('assignment.you', 'you')})
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {agent.email}
                  </span>
                </div>
                {isCurrentAgent && (
                  <Badge variant="secondary" className="text-xs">
                    {t('assignment.current', 'Current')}
                  </Badge>
                )}
              </DropdownMenuItem>
            );
          })
        )}

        {/* NO "Unassign" option - CRITICAL BUSINESS RULE */}
        {/* Once a conversation is assigned, it can NEVER go back to unassigned (NULL) */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
